import React from 'react';
import { ModalProps } from '@/Types';
import { AddressForm } from '@/Components/Forms/AddressForm/AddressForm';
import { FormProvider } from '@/Hooks/useFormWrapper';
import styles from './AddressValidationResolvingModal.scss';
import { AddressModel } from '@/Models';
import { AddressValidationResultType, ValidationType } from '@/Enums';
import { useLocalization } from '@/Hooks';
import { Button } from '@/Components/Controls/Button/Button';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { Modal } from '@/Components/Modal/Modal';
import { countryTypeUtils } from '@/Utils/CountryTypesUtils';
import { useSelector } from 'react-redux';
import { globalSelectors } from '@/Redux/Global/selectors';
import { shippingLabelModalSelectors } from '@/ModalWindows/ShippingLabelModal/services';
import { useAddressValidationForm, FormData } from './hooks/useAddressValidationForm';
import { ButtonsWrapper } from '@/Components/Forms/ButtonsSection/ButtonsSection';

type Props = ModalProps;

export function AddressValidationResolvingModal({ closeModalWindow }: Props) {
  const {
    buttons: { Cancel, Save },
    modalWindows: {
      addressValidationResolving: { AddressNotFound, Title, Warning, OriginalShippingAddress, NewShippingAddress },
    },
  } = useLocalization();

  const translation = useSelector(globalSelectors.translation);
  const currentConflict = useSelector(shippingLabelModalSelectors.currentValidationConflict);
  const currentConflictIndex = useSelector(shippingLabelModalSelectors.currentValidationConflictIndex);
  const totalConflictsCount = useSelector(shippingLabelModalSelectors.validationConflictsCount);
  const { methods, onSubmit } = useAddressValidationForm(currentConflict);

  return (
    <Modal
      modalClassName={styles.modal}
      onClose={closeModalWindow}
      shouldCloseOnOverlayClick={false}
      modalId={'addressValidationModal'}
      tourKey={'shippingLabelTour'}
    >
      <h3 className={styles.modalTitle}>
        {Title}
        <span>
          ({currentConflictIndex + 1}/{totalConflictsCount || 0})
        </span>
      </h3>
      <p className={styles.warningText}>{Warning}</p>

      {currentConflict?.result === AddressValidationResultType.NotFound && (
        <p className={styles.errorText}>{AddressNotFound}</p>
      )}

      <FormProvider {...methods}>
        <form className={styles.formWrapper} onSubmit={onSubmit}>
          <div className={styles.inputsWrapper}>
            <div>
              <h4 className={styles.formSectionTitle}>{OriginalShippingAddress}</h4>
              <AddressForm<FormData>
                disabled
                fieldsInfo={{
                  company: { value: currentConflict?.originalAddress?.company, name: 'suggestedCompany' },
                  country: {
                    value: countryTypeUtils.getCountryNameByCountryCode(
                      currentConflict?.suggestedAddress?.countryCode,
                      translation,
                    ),
                    name: 'originalCountry',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'country',
                      true,
                    ),
                  },
                  countryCode: {
                    value: currentConflict?.originalAddress?.countryCode,
                    name: 'originalCountryCode',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'countryCode',
                      true,
                    ),
                  },
                  city: {
                    value: currentConflict?.originalAddress?.city,
                    name: 'originalCity',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'city',
                      true,
                    ),
                  },
                  zip: {
                    value: currentConflict?.originalAddress?.postalCode,
                    name: 'originalPostalCode',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'postalCode',
                      true,
                    ),
                  },
                  street: {
                    value: currentConflict?.originalAddress?.street,
                    name: 'originalStreet',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'street',
                      true,
                    ),
                  },
                  houseNo: {
                    value: currentConflict?.originalAddress?.houseNo,
                    name: 'originalHouseNo',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'houseNo',
                      true,
                    ),
                  },
                  lastName: { value: currentConflict?.originalAddress?.lastName, name: 'originalLastName' },
                  firstName: { value: currentConflict?.originalAddress?.firstName, name: 'originalFirstName' },
                  additional: {
                    value: currentConflict?.originalAddress?.additionalLine,
                    name: 'originalAdditionalLine',
                  },
                  email: { value: currentConflict?.originalAddress?.email, name: 'originalEmail' },
                  phone: { value: currentConflict?.originalAddress?.phone, name: 'originalPhone' },
                }}
              />
            </div>

            <div>
              <h4 className={styles.formSectionTitle}>{NewShippingAddress}</h4>
              <AddressForm<FormData>
                fieldsInfo={{
                  company: { value: currentConflict?.suggestedAddress?.company, name: 'suggestedCompany' },
                  country: {
                    value: countryTypeUtils.getCountryNameByCountryCode(
                      currentConflict?.suggestedAddress?.countryCode,
                      translation,
                    ),
                    name: 'suggestedCountry',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'country',
                      false,
                    ),
                  },
                  countryCode: {
                    value: currentConflict?.suggestedAddress?.countryCode,
                    name: 'suggestedCountryCode',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'countryCode',
                      false,
                    ),
                  },
                  city: {
                    value: currentConflict?.suggestedAddress?.city,
                    name: 'suggestedCity',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'city',
                      false,
                    ),
                  },
                  zip: {
                    value: currentConflict?.suggestedAddress?.postalCode,
                    name: 'suggestedPostalCode',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'postalCode',
                      false,
                    ),
                  },
                  street: {
                    value: currentConflict?.suggestedAddress?.street,
                    name: 'suggestedStreet',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'street',
                      false,
                    ),
                  },
                  houseNo: {
                    value: currentConflict?.suggestedAddress?.houseNo,
                    name: 'suggestedHouseNo',
                    validationType: getValidationType(
                      currentConflict?.originalAddress,
                      currentConflict?.suggestedAddress,
                      'houseNo',
                      false,
                    ),
                  },
                  lastName: { value: currentConflict?.suggestedAddress?.lastName, name: 'suggestedLastName' },
                  firstName: { value: currentConflict?.suggestedAddress?.firstName, name: 'suggestedFirstName' },
                  additional: {
                    value: currentConflict?.suggestedAddress?.additionalLine,
                    name: 'suggestedAdditionalLine',
                  },
                  email: { value: currentConflict?.suggestedAddress?.email, name: 'suggestedEmail' },
                  phone: { value: currentConflict?.suggestedAddress?.phone, name: 'suggestedPhone' },
                }}
              />
            </div>
          </div>

          <ButtonsWrapper>
            <Button text={Cancel} theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} onClick={closeModalWindow} />
            <Button text={Save} theme={BUTTON_THEMES.SUCCESS} type={'submit'} />
          </ButtonsWrapper>
        </form>
      </FormProvider>
    </Modal>
  );
}

function getValidationType(
  originalAddress: Partial<AddressModel>,
  suggestedAddress: Partial<AddressModel>,
  key: keyof AddressModel,
  isForOriginalAddress: boolean,
): ValidationType {
  if (originalAddress?.[key]?.toLocaleLowerCase() !== suggestedAddress?.[key]?.toLocaleLowerCase()) {
    return isForOriginalAddress ? ValidationType.Invalid : ValidationType.Valid;
  }

  return ValidationType.None;
}
